/**
 * AuthProvider
 *
 * Provides authentication state and actions to the entire app via React context.
 *
 * On mount it calls GET /auth/me to restore an existing session.
 * Exposes `login(googleCredential)` and `logout()` for the login page.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import apiClient from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccessibleTenant {
  id: string;
  name: string;
  active: boolean;
  role: 'tenant_owner' | 'tenant_member';
}

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  role: string;
  tenantId?: string | null;
  tenantName?: string | null;
  /** Tenants the caller can switch into (admins see every tenant). */
  accessibleTenants?: AccessibleTenant[];
}

export interface AuthContextValue {
  /** Currently authenticated user, or null. */
  user: User | null;
  /** True while the initial session check is in progress. */
  isLoading: boolean;
  /** Whether the user is authenticated. */
  isAuthenticated: boolean;
  /** Convenience: user has the platform_admin role. */
  isAdmin: boolean;
  /** Convenience: user has the tenant_owner role. */
  isTenantOwner: boolean;
  /** Sign in with a Google credential (id_token or access_token). */
  login: (googleCredential: string, tokenType?: 'id_token' | 'access_token') => Promise<void>;
  /** Development-only login bypass — sign in with just an email. */
  devLogin: (email: string) => Promise<void>;
  /** Sign out and clear state. */
  logout: () => Promise<void>;
  /** Switch active tenant context. Reloads user payload. */
  switchTenant: (tenantId: string | null) => Promise<User | null>;
  /** Last error from login/session check. */
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Restore session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/auth/me');
        const userData = res.data?.data?.user || res.data?.user || res.data;
        if (!cancelled && userData?.id) setUser(userData);
      } catch {
        // Not authenticated — that is fine
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (googleCredential: string, tokenType: 'id_token' | 'access_token' = 'id_token') => {
    setError(null);
    try {
      const payload = tokenType === 'access_token'
        ? { access_token: googleCredential }
        : { id_token: googleCredential };
      const { data } = await apiClient.post('/auth/google', payload);
      // The server returns { success, data: { user } }
      const userData = data?.data?.user || data?.user || data;
      setUser(userData);
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Login failed. Please try again.';
      setError(message);
      throw err;
    }
  }, []);

  const devLogin = useCallback(async (email: string) => {
    setError(null);
    try {
      const { data } = await apiClient.post('/auth/dev-login', { email });
      const userData = data?.data?.user || data?.user || data;
      setUser(userData);
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Dev login failed.';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Best-effort
    }
    setUser(null);
  }, []);

  const switchTenant = useCallback(async (tenantId: string | null) => {
    try {
      const { data } = await apiClient.post('/auth/switch-tenant', { tenantId });
      const userData = data?.data?.user || data?.user || data;
      setUser(userData);
      return userData as User;
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Failed to switch tenant.';
      setError(message);
      throw err;
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isAuthenticated = user !== null;
    return {
      user,
      isLoading,
      isAuthenticated,
      isAdmin: user?.role === 'platform_admin',
      isTenantOwner: user?.role === 'tenant_owner',
      login,
      devLogin,
      logout,
      switchTenant,
      error,
    };
  }, [user, isLoading, error, login, devLogin, logout, switchTenant]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the authentication context. Must be used within an `<AuthProvider>`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
